/**
 * Privacy Policy Page Component
 * GDPR-compliant privacy policy display
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Database, Lock, Cookie, UserCheck, Mail, Bot } from 'lucide-react';
import ShieldIcon from './ShieldIcon';

const PrivacyPolicy: React.FC = () => {
    const { t } = useTranslation();
    
    const sections = [
        {
            key: 'dataCollection',
            icon: Database,
        },
        {
            key: 'dataUsage',
            icon: Shield,
        },
        {
            key: 'aiServices',
            icon: Bot,
        },
        {
            key: 'dataSecurity',
            icon: Lock,
        },
        {
            key: 'cookies',
            icon: Cookie,
        },
        {
            key: 'yourRights',
            icon: UserCheck,
        },
        {
            key: 'contact',
            icon: Mail,
        },
    ];
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <Link
                        to="/"
                        className="mb-6 inline-flex items-center text-secondary-600 transition-colors hover:text-brand-500"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {t('common.back')}
                    </Link>
                    
                    <div className="flex items-center mb-4">
                        <span className="mr-4 grid h-11 w-11 shrink-0 overflow-hidden rounded-full bg-brand-500 shadow-sm ring-2 ring-brand-500/25 sm:h-12 sm:w-12">
                            <ShieldIcon className="size-full" />
                        </span>
                        <div>
                            <h1 className="text-3xl font-bold text-secondary-800">
                                {t('privacy.title')}
                            </h1>
                            <p className="text-secondary-500 mt-1">
                                {t('privacy.lastUpdated')} January 2026
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Content */}
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="prose prose-secondary max-w-none">
                        <p className="text-lg text-secondary-600 mb-8">
                            At QuickMask, we take your privacy seriously. This policy explains how we collect, 
                            use, and protect your personal information when you use our PDF redaction services.
                        </p>
                        
                        {sections.map((section, index) => {
                            const Icon = section.icon;
                            return (
                                <div key={section.key} className={index !== 0 ? 'mt-8' : ''}>
                                    <div className="flex items-center mb-4">
                                        <div className="mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-500/12">
                                            <Icon className="h-5 w-5 text-brand-500" />
                                        </div>
                                        <h2 className="text-xl font-semibold text-secondary-800 m-0">
                                            {t(`privacy.sections.${section.key}.title`)}
                                        </h2>
                                    </div>
                                    <p className="text-secondary-600 ml-12">
                                        {t(`privacy.sections.${section.key}.content`)}
                                    </p>
                                </div>
                            );
                        })}
                        
                        {/* Additional GDPR Information */}
                        <div className="mt-10 rounded-xl border border-brand-500/20 bg-brand-500/10 p-6">
                            <h3 className="mb-3 text-lg font-semibold text-brand-800">
                                Your Data Rights Under GDPR
                            </h3>
                            <ul className="space-y-2 text-secondary-700">
                                <li className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span><strong>Right to Access:</strong> Request a copy of your personal data</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span><strong>Right to Rectification:</strong> Correct inaccurate personal data</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span><strong>Right to Erasure:</strong> Request deletion of your personal data</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span><strong>Right to Data Portability:</strong> Export your data in a machine-readable format</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</span>
                                </li>
                            </ul>
                        </div>
                        
                        {/* Data Retention */}
                        <div className="mt-8">
                            <h2 className="text-xl font-semibold text-secondary-800 mb-4">
                                Data Retention and Deletion
                            </h2>
                            <div className="text-secondary-600 space-y-4">
                                <p>
                                    We retain your account details and the documents and related data you upload or
                                    generate in QuickMask only for as long as you maintain them in the service. When you
                                    use the product&apos;s deletion features to remove a document or other
                                    content, or when you delete your account, we will delete the associated files and
                                    associated metadata from our production systems. After that deletion process
                                    completes, we do not use deleted content for product improvement, analytics, or
                                    marketing, and it is not available to you or to us through the application.
                                </p>
                                <p>
                                    Deletion is intended to be permanent with respect to our active servers and primary
                                    storage. Like many cloud services, residual copies (for example, in encrypted backups)
                                    may persist for a limited technical period under our infrastructure provider&apos;s
                                    retention policies before they age out; such copies are not used to restore your
                                    deleted content to the live service except where we are legally required to preserve
                                    data (for example, in response to lawful process or a bona fide legal hold).
                                </p>
                            </div>
                        </div>
                        
                        {/* Updates to Policy */}
                        <div className="mt-8">
                            <h2 className="text-xl font-semibold text-secondary-800 mb-4">
                                Updates to This Policy
                            </h2>
                            <p className="text-secondary-600">
                                We may update this privacy policy from time to time. We will notify you of any 
                                significant changes by email or through a notice on our website.
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Footer */}
                <div className="mt-8 text-center text-secondary-500 text-sm">
                    <p>
                        If you have any questions about this privacy policy, please contact us at{' '}
                        <a
                            href="mailto:privacy@quickmask.com"
                            className="font-medium text-brand-500 underline-offset-2 transition-colors hover:text-brand-600 hover:underline"
                        >
                            privacy@quickmask.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
